type Labels = Record<string, string> | null | undefined;
type Properties = Record<string, unknown> | null | undefined;

export type TopologyPolicyNodeSummary = {
  subtitle?: string;
  badges: string[];
};

export type TopologyEmbeddedPolicyItem = {
  key: string;
  title: string;
  subtitle?: string;
  chips: string[];
  rawValue: Record<string, unknown>;
};

export type TopologyEmbeddedPolicySection = {
  title: string;
  items: TopologyEmbeddedPolicyItem[];
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
};

const getString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const getNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const formatTitle = (value: string): string =>
  value
    .split('_')
    .filter(Boolean)
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');

const formatCompactList = (value: unknown, limit = 2): string | undefined => {
  if (Array.isArray(value)) {
    const rendered = value.map((item) => getString(item) ?? String(item ?? '')).filter(Boolean);
    if (rendered.length === 0) {
      return undefined;
    }
    if (rendered.length <= limit) {
      return rendered.join(', ');
    }
    return `${rendered.slice(0, limit).join(', ')} +${rendered.length - limit}`;
  }
  return getString(value);
};

const pluralize = (count: number, singular: string, plural = `${singular}s`): string => `${count} ${count === 1 ? singular : plural}`;

const policySetKindLabel = (kind: string | undefined): string | undefined => {
  switch ((kind ?? '').trim().toLowerCase()) {
    case 'security_policy_set':
      return 'Security policy';
    case 'nat_policy_set':
      return 'NAT policy';
    case 'acl_policy_set':
      return 'ACL policy';
    case 'vsys':
      return 'Virtual system';
    case 'zone':
      return 'Security zone';
    default:
      return kind ? formatTitle(kind) : undefined;
  }
};

export const inferTopologyPolicyNodeSummary = (
  labels: Labels,
  properties: Properties
): TopologyPolicyNodeSummary => {
  const props = properties ?? undefined;
  const level = getString(labels?.level)?.toLowerCase();
  const domain = getString(labels?.domain)?.toLowerCase();
  const kind = getString(props && 'kind' in props ? props.kind : undefined);
  const vsys = getString(props && 'vsys' in props ? props.vsys : undefined);
  const ruleCount = getNumber(props && 'rule_count' in props ? props.rule_count : undefined);
  const badges: string[] = [];

  if (typeof ruleCount === 'number' && ruleCount >= 0) {
    badges.push(pluralize(ruleCount, 'rule'));
  }

  if (level === 'policy' || (kind ?? '').endsWith('_policy_set')) {
    const subtitleParts = [policySetKindLabel(kind)];
    if (vsys) {
      subtitleParts.push(`VSYS ${vsys}`);
    }
    return {
      subtitle: subtitleParts.filter(Boolean).join(' · ') || undefined,
      badges
    };
  }

  if (level === 'domain' && domain === 'vsys') {
    return {
      subtitle: vsys ? `Virtual system · ${vsys}` : 'Virtual system',
      badges
    };
  }

  if (level === 'domain' && domain === 'security' && kind === 'zone') {
    return {
      subtitle: vsys ? `Security zone · VSYS ${vsys}` : 'Security zone',
      badges
    };
  }

  return { badges };
};

const extractObjectArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.map((item) => asRecord(item)).filter((item): item is Record<string, unknown> => Boolean(item)) : [];

const policyItemTitle = (item: Record<string, unknown>, index: number, fallbackLabel: string): string => {
  return (
    getString(item.original_name) ??
    getString(item.name) ??
    getString(item.id) ??
    `${fallbackLabel} ${index + 1}`
  );
};

const policyItemSubtitle = (item: Record<string, unknown>): string | undefined => {
  const parts: string[] = [];
  const stableName = getString(item.name);
  const originalName = getString(item.original_name);
  const sequence = getNumber(item.sequence);
  const ruleNumber = getNumber(item.ruleNumber ?? item.rule_number);
  const action = getString(item.action);
  const direction = getString(item.direction);

  if (stableName && originalName && stableName !== originalName) {
    parts.push(stableName);
  }
  if (typeof sequence === 'number') {
    parts.push(`seq ${sequence}`);
  } else if (typeof ruleNumber === 'number') {
    parts.push(`rule ${ruleNumber}`);
  }
  if (direction) {
    parts.push(direction);
  }
  if (action) {
    parts.push(action);
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

const policyItemChips = (item: Record<string, unknown>): string[] => {
  const chips: string[] = [];
  const candidates: Array<[string, unknown]> = [
    ['from', item.from ?? item.source_zone],
    ['to', item.to ?? item.destination_zone],
    ['service', item.service],
    ['application', item.application],
    ['protocol', item.protocol],
    ['translated', item.translated_to ?? item.translation],
  ];
  candidates.forEach(([label, raw]) => {
    const rendered = formatCompactList(raw);
    if (rendered) {
      chips.push(`${label}: ${rendered}`);
    }
  });
  return chips.slice(0, 5);
};

export const extractTopologyEmbeddedPolicySection = (properties: Properties): TopologyEmbeddedPolicySection | null => {
  const props = properties ?? undefined;
  if (!props) {
    return null;
  }

  const rules = extractObjectArray(props.rules);
  const entries = rules.length === 0 ? extractObjectArray(props.entries) : [];
  const items = rules.length > 0 ? rules : entries;
  if (items.length === 0) {
    return null;
  }

  const title = rules.length > 0 ? 'Rules' : 'Entries';
  const fallbackLabel = rules.length > 0 ? 'Rule' : 'Entry';

  return {
    title,
    items: items.map((item, index) => ({
      key: `${getString(item.name) ?? getString(item.id) ?? fallbackLabel.toLowerCase()}-${index + 1}`,
      title: policyItemTitle(item, index, fallbackLabel),
      subtitle: policyItemSubtitle(item),
      chips: policyItemChips(item),
      rawValue: item
    }))
  };
};
